/**
 * 🟠 [Phase 56.5 — 2026-07-09] Dialog "Affecter un employé sans-job à un BT"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L2107-2183).
 *
 * Rôle : dans le panneau Sans job, l'utilisatrice clique sur "+ Affecter" pour
 * un employé, cette modal liste les BT actifs du jour pour choisir la cible.
 * Note : implémenté en overlay `div fixed` custom (pas Radix Dialog) parce que
 * le legacy utilise un backdrop cliquable pour fermer.
 *
 * Contrat :
 *   - `data` : { date, employee_id, employee_nom } OU null
 *   - `bts` : liste des BT actifs (affecterBts)
 *   - `chosenBtId` : ID du BT choisi
 *   - `onChoose(id)` : callback sélection radio
 *   - `loading` : booléen affecterLoading (désactive boutons)
 *   - `onCancel()` : ferme sans agir
 *   - `onSubmit()` : appelle submitAffecter du parent
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
export default function AffecterEmployeDialog({
  data, bts = [], chosenBtId, onChoose, loading, onCancel, onSubmit,
}) {
  if (!data) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={() => !loading && onCancel?.()}
      data-testid="dialog-affecter-overlay"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="dialog-affecter-employe"
      >
        <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <span>🟠</span>
          Affecter à un BT
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          <b>{data.employee_nom}</b> · {data.date}
        </p>

        {bts.length === 0 ? (
          <div className="text-center py-6 text-sm text-slate-500">
            Aucun BT actif ce jour. Créez d'abord un bon de travail pour le {data.date}.
          </div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="liste-bts-affecter">
            {bts.map((b) => (
              <label
                key={b.bt_id}
                className={`flex items-start gap-2 p-2.5 rounded border cursor-pointer transition ${chosenBtId === b.bt_id ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'}`}
                data-testid={`option-bt-${b.bt_id}`}
              >
                <input
                  type="radio"
                  name="bt-choice"
                  value={b.bt_id}
                  checked={chosenBtId === b.bt_id}
                  onChange={(e) => onChoose?.(e.target.value)}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{b.client_nom}</p>
                  <div className="flex flex-wrap gap-2 text-[11px] text-slate-500 mt-0.5">
                    {b.chef_equipe_nom && <span>👑 {b.chef_equipe_nom}</span>}
                    {b.type_travaux && <span>· {b.type_travaux}</span>}
                    {b.est_interne && <span className="text-purple-600">· interne</span>}
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onCancel?.()}
            disabled={loading}
            className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            data-testid="btn-affecter-annuler"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => onSubmit?.()}
            disabled={loading || !chosenBtId}
            className="px-4 py-2 text-sm rounded bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
            data-testid="btn-affecter-confirmer"
          >
            {loading ? 'Affectation…' : 'Affecter'}
          </button>
        </div>
      </div>
    </div>
  );
}
